import { useMemo } from 'react'
import { toast } from 'react-toastify'

import {
  CompanyModal,
  CompanyFormShape,
} from 'components/Modals/CompanyModal/CompanyModal'
import { IContactCompany } from 'models/contact_companies'
import useApiService from 'services/api'

const OTHER_CATEGORY_SLUG = 'other'
const OTHER_SUB_CATEGORY_SLUG = 'other-other'

interface QuickCreateCompanyModalProps {
  open: boolean
  onClose: () => void
  onCompanyCreated: (company: IContactCompany) => void
}

/**
 * Wraps CompanyModal for the sample portal's "quick create" flow. Hides the
 * category/sub-category picker, domain uniqueness check, distributors, tags,
 * and the follow-on deal modal so a broker can create a ship-to company with
 * only name + address fields. Auto-assigns the Other/Other category so BIH
 * can audit/reclassify later via the `Created By` filter.
 */
export function QuickCreateCompanyModal({
  open,
  onClose,
  onCompanyCreated,
}: QuickCreateCompanyModalProps) {
  const api = useApiService()
  // Use the unfiltered categories endpoint — `useGetActiveCompanyCategoriesAndTypes`
  // omits categories that no company in the tenant uses, which hides Other on
  // fresh tenants and breaks this modal's default-category lookup.
  const { data: categories } = api.useGetCompanyCategories()

  const defaults = useMemo(() => {
    if (!categories) return null
    const category = categories.find((c) => c.slug === OTHER_CATEGORY_SLUG)
    if (!category) return null
    const sub = category.sub_categories.find(
      (s) => s.slug === OTHER_SUB_CATEGORY_SLUG
    )
    if (!sub) return null
    return { category: category.id, sub_category: sub.id }
  }, [categories])

  const apiAction = async (values: CompanyFormShape) => {
    if (!defaults) {
      toast.error('Unable to save company. Please try again.')
      return
    }
    const payload = {
      ...values,
      category: defaults.category,
      sub_category: defaults.sub_category,
      country: values.country || 'US',
      state: values.state || '',
      city: values.city || '',
      domain: values.domain?.toLowerCase() || '',
      name: values.name?.trim() || '',
      address: values.address || '',
      zipcode: values.zipcode || '',
    }
    const response = await api.createCompany(payload)
    toast.success('Company created')
    onCompanyCreated(response)
    return response
  }

  return (
    <CompanyModal
      show={open}
      setShow={(isOpen: boolean) => {
        if (!isOpen) onClose()
      }}
      title="New Company"
      description="Add a company to ship a sample to."
      acceptButtonText="Create"
      defaultValues={{ country: 'US' }}
      hideCategoryAndType
      hideKnownDistributorsAndTags
      hideGooglePlaceUrl
      skipDealCreation
      checkUniqueDomain={false}
      apiAction={apiAction}
    />
  )
}
